/**
 * Rajapintakutsut yhdessä paikassa.
 *
 * Kyselyavaimet ovat hierarkkisia (`['time-entries', from, to]`), jotta
 * yhden kirjauksen tallennus mitätöi koko kalenterijakson kerralla eikä
 * jokaista päivää erikseen.
 */

import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type {
  SecretCreateInput,
  SecretListQuery,
  SecretState,
  SharedSecretCreatedDto,
  SharedSecretDto,
  AdminSettingsDto,
  AttributeDefinitionDto,
  BoardKeyDto,
  BoardListResponse,
  BoardResponse,
  CustomerReportDto,
  CustomerReportQuery,
  KanbanTaskCommentDto,
  MyTasksResponse,
  PartnerDto,
  TaskHistoryDto,
  TaskHoursDto,
  TaskLookupDto,
  TaskRefDto,
  TaskRelationDto,
  TaskReminderDto,
  AgentTokenCreatedDto,
  AgentTokenCreateInput,
  ApiTokenCreatedDto,
  ApiTokenCreateInput,
  ApiTokenDto,
  AppSettingsPublic,
  AuditLogEntryDto,
  BrandingDto,
  CheckStateDto,
  CustomerColorDto,
  CustomerContactDto,
  CustomerDto,
  CustomerLinkDto,
  CustomerLinkTagDto,
  CertificationDto,
  CertificationReportDto,
  DevelopmentOverviewDto,
  DevelopmentPathDto,
  DevelopmentReviewDto,
  MyDevelopmentDto,
  UserDevelopmentDto,
  EntryNoteDto,
  EntryNoteListItemDto,
  EntrySuggestionConfirmInput,
  EntrySuggestionConfirmResponse,
  EntrySuggestionDto,
  ExamAnswerInput,
  ExamAttemptDto,
  ExamResultDto,
  LockChecklistDto,
  MonthLockEventDto,
  MonthLockSummaryDto,
  MySummaryDto,
  IsoDate,
  Paginated,
  ProjectDto,
  ProjectListRowDto,
  ProjectSummaryDto,
  RangeBoundsDto,
  RateOverviewDto,
  RateRecalculateInput,
  RateRecalculationDto,
  ReminderKind,
  LogoUploadInput,
  LogoVariant,
  RecentCombinationDto,
  ReminderPreviewDto,
  ReminderSettingsDto,
  ReportCustomerDetailDto,
  ReportGroupBy,
  ReportSummaryDto,
  SettingsUpdateInput,
  UtilizationReportDto,
  UtilizationUserDetailDto,
  TaskTypeDto,
  TaskTypeTemplateApplyResultDto,
  TaskTypeTemplateGapsDto,
  TimeEntriesResponse,
  TimeEntryCreateInput,
  TimeEntryMutationResponse,
  TimeEntryUpdateInput,
  TrainingAdminDto,
  TrainingAlertsDto,
  TrainingAssignmentsInput,
  TrainingAudienceDto,
  TrainingCompletionRecordInput,
  TrainingDetailDto,
  TrainingListItemDto,
  TrainingReportDto,
  TrainingSettingsInput,
  TrainingSyncResultDto,
  UserDto,
} from '@tk/shared';
import { api, type ApiError } from './api';

export const queryKeys = {
  timeEntries: (from: IsoDate, to: IsoDate) => ['time-entries', from, to] as const,
  entrySuggestions: (from: IsoDate, to: IsoDate) => ['entry-suggestions', from, to] as const,
  recentCombinations: ['time-entries', 'recent-combinations'] as const,
  customers: (query: string, mine: boolean) => ['customers', query, mine] as const,
  customer: (id: string) => ['customers', id] as const,
  customerColors: ['customers', 'colors'] as const,
  taskTypes: (customerId: string, includeInactive: boolean) =>
    ['customers', customerId, 'task-types', includeInactive] as const,
  projects: (customerId: string) => ['customers', customerId, 'projects'] as const,
  projectSummary: (id: string) => ['projects', id, 'summary'] as const,
  users: ['users'] as const,
  assignments: (customerId: string) => ['customers', customerId, 'assignments'] as const,
  attributeDefinitions: ['attribute-definitions'] as const,
};

// --- Kalenteri -------------------------------------------------------------

export function useTimeEntries(
  from: IsoDate,
  to: IsoDate,
): UseQueryResult<TimeEntriesResponse, ApiError> {
  return useQuery<TimeEntriesResponse, ApiError>({
    queryKey: queryKeys.timeEntries(from, to),
    queryFn: () => api.get<TimeEntriesResponse>(`/api/time-entries?from=${from}&to=${to}`),
  });
}

export function useRecentCombinations(): UseQueryResult<RecentCombinationDto[], ApiError> {
  return useQuery<RecentCombinationDto[], ApiError>({
    queryKey: queryKeys.recentCombinations,
    queryFn: () => api.get<RecentCombinationDto[]>('/api/time-entries/recent-combinations'),
    staleTime: 5 * 60 * 1000,
  });
}

/** Kalenterin ja pikavalintojen mitätöinti yhdellä kutsulla. */
function useInvalidateEntries() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['time-entries'] });
    void queryClient.invalidateQueries({ queryKey: ['projects'] });
    // Ehdotukset mukaan: vahvistus poistaa ehdotuksen listalta, ja myös
    // käsin tehty kirjaus voi tehdä ehdotuksesta tarpeettoman. Kalenteri
    // näyttää molemmat samassa sarakkeessa, joten niiden on päivityttävä
    // yhdessä — muuten ruudulle jää ehdotus, jota ei enää ole.
    void queryClient.invalidateQueries({ queryKey: ['entry-suggestions'] });
  };
}

export function useCreateEntry() {
  const invalidate = useInvalidateEntries();
  return useMutation<TimeEntryMutationResponse, ApiError, TimeEntryCreateInput>({
    mutationFn: (input) => api.post<TimeEntryMutationResponse>('/api/time-entries', input),
    onSuccess: invalidate,
  });
}

export function useUpdateEntry() {
  const invalidate = useInvalidateEntries();
  return useMutation<
    TimeEntryMutationResponse,
    ApiError,
    { id: string; input: TimeEntryUpdateInput }
  >({
    mutationFn: ({ id, input }) =>
      api.patch<TimeEntryMutationResponse>(`/api/time-entries/${id}`, input),
    onSuccess: invalidate,
  });
}

/** Kopioi edellinen päivä (suunnitelma 10). Palvelin ratkaisee hinnat uudelleen. */
export function useCopyDay() {
  const invalidate = useInvalidateEntries();
  return useMutation<
    { created: number; warnings: Array<{ message: string }> },
    ApiError,
    { from: IsoDate; to: IsoDate }
  >({
    mutationFn: (input) =>
      api.post<{ created: number; warnings: Array<{ message: string }> }>(
        '/api/time-entries/copy-day',
        input,
      ),
    onSuccess: invalidate,
  });
}

export function useDeleteEntry() {
  const invalidate = useInvalidateEntries();
  return useMutation<void, ApiError, string>({
    mutationFn: (id) => api.delete<void>(`/api/time-entries/${id}`),
    onSuccess: invalidate,
  });
}

// --- Kirjausehdotukset (backlog 5) -----------------------------------------

/**
 * Avoimet ehdotukset kalenterijaksolle.
 *
 * Oma kyselynsä eikä osa `useTimeEntries`ia: ehdotus ei ole kirjaus, eikä
 * niitä pidä saada samasta vastauksesta, jossa ne voisivat vahingossa
 * päätyä samaan summaan. Sama ero kuin palvelimella, jossa ehdotukset ovat
 * omassa taulussaan.
 */
export function useEntrySuggestions(
  from: IsoDate,
  to: IsoDate,
): UseQueryResult<EntrySuggestionDto[], ApiError> {
  return useQuery<EntrySuggestionDto[], ApiError>({
    queryKey: queryKeys.entrySuggestions(from, to),
    queryFn: () => api.get<EntrySuggestionDto[]>(`/api/entry-suggestions?from=${from}&to=${to}`),
  });
}

/** Vahvistus luo kirjauksen, joten se mitätöi kalenterin kuten muutkin. */
export function useConfirmSuggestion() {
  const invalidate = useInvalidateEntries();
  return useMutation<
    EntrySuggestionConfirmResponse,
    ApiError,
    { id: string; input: EntrySuggestionConfirmInput }
  >({
    mutationFn: ({ id, input }) =>
      api.post<EntrySuggestionConfirmResponse>(`/api/entry-suggestions/${id}/confirm`, input),
    onSuccess: invalidate,
  });
}

export function useDismissSuggestion() {
  const invalidate = useInvalidateEntries();
  return useMutation<EntrySuggestionDto, ApiError, string>({
    mutationFn: (id) => api.delete<EntrySuggestionDto>(`/api/entry-suggestions/${id}`),
    onSuccess: invalidate,
  });
}

// --- Omat agenttitunnukset (backlog 5) -------------------------------------

/**
 * Omat agenttitunnukset. Erillään ylläpitäjän `useApiTokens`ista, koska
 * pääte on eri: täällä näkee vain omansa, eikä listaus vaadi
 * ylläpitäjän oikeuksia.
 */
export function useAgentTokens(): UseQueryResult<ApiTokenDto[], ApiError> {
  return useQuery<ApiTokenDto[], ApiError>({
    queryKey: ['agent-tokens'],
    queryFn: () => api.get<ApiTokenDto[]>('/api/me/agent-tokens'),
  });
}

/** Kuten `useCreateApiToken`: vastausta ei laiteta välimuistiin. */
export function useCreateAgentToken() {
  const queryClient = useQueryClient();
  return useMutation<AgentTokenCreatedDto, ApiError, AgentTokenCreateInput>({
    mutationFn: (input) => api.post<AgentTokenCreatedDto>('/api/me/agent-tokens', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['agent-tokens'] });
    },
  });
}

export function useRevokeAgentToken() {
  const queryClient = useQueryClient();
  return useMutation<ApiTokenDto, ApiError, string>({
    mutationFn: (id) => api.delete<ApiTokenDto>(`/api/me/agent-tokens/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['agent-tokens'] });
    },
  });
}

// --- Perustiedot -----------------------------------------------------------

export function useCustomerSearch(
  query: string,
  options: { mine?: boolean; enabled?: boolean; includeInactive?: boolean } = {},
): UseQueryResult<Paginated<CustomerDto>, ApiError> {
  const mine = options.mine ?? false;
  const params = new URLSearchParams({ pageSize: '50' });
  if (query) params.set('q', query);
  if (mine) params.set('mine', 'true');
  if (options.includeInactive) params.set('active', 'false');

  return useQuery<Paginated<CustomerDto>, ApiError>({
    queryKey: [...queryKeys.customers(query, mine), options.includeInactive ?? false],
    queryFn: () => api.get<Paginated<CustomerDto>>(`/api/customers?${params.toString()}`),
    enabled: options.enabled ?? true,
    staleTime: 60 * 1000,
  });
}

export function useCustomer(id: string | null): UseQueryResult<CustomerDto, ApiError> {
  return useQuery<CustomerDto, ApiError>({
    queryKey: queryKeys.customer(id ?? ''),
    queryFn: () => api.get<CustomerDto>(`/api/customers/${id}`),
    enabled: Boolean(id),
  });
}

/**
 * Aktiivisten asiakkaiden sävytilanne sävyvalitsinta varten (10).
 *
 * Ainoa pääte, joka antaa koko asiakasluettelon kerralla. Se on perusteltua
 * vain tässä: valitsimen on kerrottava, ketkä muut ovat samassa sävyssä, ja
 * se on koko joukon ominaisuus eikä sivun. Vastaus on kapea — id, koodi,
 * nimi ja valinta — eikä siinä ole hintoja.
 */
export function useCustomerColors(enabled: boolean): UseQueryResult<CustomerColorDto[], ApiError> {
  return useQuery<CustomerColorDto[], ApiError>({
    queryKey: queryKeys.customerColors,
    queryFn: () => api.get<CustomerColorDto[]>('/api/customers/colors'),
    enabled,
  });
}

export function useTaskTypes(
  customerId: string | null,
  options: { includeInactive?: boolean } = {},
): UseQueryResult<TaskTypeDto[], ApiError> {
  const includeInactive = options.includeInactive ?? false;
  return useQuery<TaskTypeDto[], ApiError>({
    queryKey: queryKeys.taskTypes(customerId ?? '', includeInactive),
    queryFn: () =>
      api.get<TaskTypeDto[]>(
        `/api/customers/${customerId}/task-types${includeInactive ? '?active=false' : ''}`,
      ),
    enabled: Boolean(customerId),
  });
}

/**
 * Pohjan ja asiakkaiden ero (backlog 2.11). Ei `staleTime`ä: näkymä on auki
 * juuri sitä varten, että levityksen jälkeen luvun on muututtava nollaksi.
 */
export function useTaskTypeTemplateGaps(
  enabled: boolean,
): UseQueryResult<TaskTypeTemplateGapsDto, ApiError> {
  return useQuery<TaskTypeTemplateGapsDto, ApiError>({
    queryKey: ['task-type-template-gaps'],
    queryFn: () => api.get<TaskTypeTemplateGapsDto>('/api/task-types/template-gaps'),
    enabled,
  });
}

export function useApplyTaskTypeTemplate() {
  const queryClient = useQueryClient();
  return useMutation<TaskTypeTemplateApplyResultDto, ApiError, string[]>({
    mutationFn: (customerIds) =>
      api.post<TaskTypeTemplateApplyResultDto>('/api/task-types/apply-template', { customerIds }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['task-type-template-gaps'] });
      // Tehtävälajit ovat asiakkaan alla (`queryKeys.taskTypes`), joten
      // mitätöinti osuu asiakasavaimeen eikä omaan avaimeensa.
      void queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}

export function useProjects(customerId: string | null): UseQueryResult<ProjectDto[], ApiError> {
  return useQuery<ProjectDto[], ApiError>({
    queryKey: queryKeys.projects(customerId ?? ''),
    queryFn: () => api.get<ProjectDto[]>(`/api/customers/${customerId}/projects`),
    enabled: Boolean(customerId),
  });
}

/** Projektilista seurantatietoineen (12.2) — hallintanäkymän sarakkeet. */
export function useProjectList(
  customerId: string | null,
): UseQueryResult<ProjectListRowDto[], ApiError> {
  return useQuery<ProjectListRowDto[], ApiError>({
    queryKey: ['projects', 'list', customerId ?? ''],
    queryFn: () => api.get<ProjectListRowDto[]>(`/api/projects?customerId=${customerId}`),
    enabled: Boolean(customerId),
  });
}

export function useProjectSummary(
  projectId: string | null,
): UseQueryResult<ProjectSummaryDto, ApiError> {
  return useQuery<ProjectSummaryDto, ApiError>({
    queryKey: queryKeys.projectSummary(projectId ?? ''),
    queryFn: () => api.get<ProjectSummaryDto>(`/api/projects/${projectId}/summary`),
    enabled: Boolean(projectId),
  });
}

export function useUsers(): UseQueryResult<UserDto[], ApiError> {
  return useQuery<UserDto[], ApiError>({
    queryKey: queryKeys.users,
    queryFn: () => api.get<UserDto[]>('/api/users'),
  });
}

export function useAssignments(
  customerId: string | null,
): UseQueryResult<Array<{ id: string; name: string; email: string }>, ApiError> {
  return useQuery<Array<{ id: string; name: string; email: string }>, ApiError>({
    queryKey: queryKeys.assignments(customerId ?? ''),
    queryFn: () =>
      api.get<Array<{ id: string; name: string; email: string }>>(
        `/api/customers/${customerId}/assignments`,
      ),
    enabled: Boolean(customerId),
  });
}

// --- Attribuutit -----------------------------------------------------------

/**
 * Attribuuttimäärittelyt.
 *
 * Haetaan kaikille kirjautuneille, koska raportti- ja vientinäkymä
 * tarvitsevat otsikot ja avaimet — ei vain hallinta. Lista on lyhyt ja
 * muuttuu harvoin, joten se saa vanhentua pitkään.
 */
export function useAttributeDefinitions(): UseQueryResult<AttributeDefinitionDto[], ApiError> {
  return useQuery<AttributeDefinitionDto[], ApiError>({
    queryKey: queryKeys.attributeDefinitions,
    queryFn: () => api.get<AttributeDefinitionDto[]>('/api/attribute-definitions'),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Määrittelyn muutos mitätöi myös asiakkaat ja projektit: avaimen otsikko
 * ja kohde näkyvät niiden riveillä, eikä vanha otsikko saa jäädä ruutuun.
 */
export function useAttributeDefinitionMutation<TResult, TInput>(
  mutate: (input: TInput) => Promise<TResult>,
) {
  const queryClient = useQueryClient();
  return useMutation<TResult, ApiError, TInput>({
    mutationFn: mutate,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.attributeDefinitions });
      void queryClient.invalidateQueries({ queryKey: ['customers'] });
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
      void queryClient.invalidateQueries({ queryKey: ['reports'] });
    },
  });
}

/**
 * Perustietojen muutokset mitätöivät myös kalenterin: hinnat ovat mukana.
 * Ja raportin: asiakkaan nimi ja attribuutit näkyvät sen otsikossa.
 */
export function useMasterDataMutation<TResult, TInput>(
  mutate: (input: TInput) => Promise<TResult>,
) {
  const queryClient = useQueryClient();
  return useMutation<TResult, ApiError, TInput>({
    mutationFn: mutate,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['customers'] });
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      void queryClient.invalidateQueries({ queryKey: ['reports'] });
      // Kalenteri näyttää asiakkaan nimen, koodin ja sävyn kirjauksen
      // mukana. Ilman näitä muutos jäisi näkymättä `staleTime`n verran,
      // ja sävyn vaihtanut ylläpitäjä vaihtaisi sen uudelleen luullen,
      // ettei ensimmäinen mennyt läpi.
      void queryClient.invalidateQueries({ queryKey: ['time-entries'] });
      void queryClient.invalidateQueries({ queryKey: ['entry-suggestions'] });
      void queryClient.invalidateQueries({ queryKey: ['entry-notes'] });
    },
  });
}

// --- Huomautukset (backlog 3.5) -------------------------------------------

export const noteKeys = {
  list: (from: IsoDate, to: IsoDate) => ['entry-notes', from, to] as const,
};

/**
 * Avoimet huomautukset jaksolta. Kalenteri näyttää ne omana listanaan:
 * kortille mahtuu merkki, muttei tekstiä eikä kuittausnappia.
 */
export function useEntryNotes(
  from: IsoDate,
  to: IsoDate,
): UseQueryResult<EntryNoteListItemDto[], ApiError> {
  return useQuery<EntryNoteListItemDto[], ApiError>({
    queryKey: noteKeys.list(from, to),
    queryFn: () => api.get<EntryNoteListItemDto[]>(`/api/entry-notes?from=${from}&to=${to}`),
  });
}

/** Huomautuksen muutos mitätöi myös kirjaukset: kortin merkki tulee niistä. */
function mitatoiHuomautukset(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: ['entry-notes'] });
  void queryClient.invalidateQueries({ queryKey: ['time-entries'] });
  void queryClient.invalidateQueries({ queryKey: ['reports'] });
}

export function useCreateEntryNote() {
  const queryClient = useQueryClient();
  return useMutation<
    { note: EntryNoteDto; mailed: boolean },
    ApiError,
    { entryId: string; text: string }
  >({
    mutationFn: ({ entryId, text }) =>
      api.post<{ note: EntryNoteDto; mailed: boolean }>(`/api/time-entries/${entryId}/notes`, {
        text,
      }),
    onSuccess: () => mitatoiHuomautukset(queryClient),
  });
}

export function useResolveEntryNote() {
  const queryClient = useQueryClient();
  return useMutation<EntryNoteDto, ApiError, { id: string; resolved: boolean }>({
    mutationFn: ({ id, resolved }) =>
      api.patch<EntryNoteDto>(`/api/entry-notes/${id}`, { resolved }),
    onSuccess: () => mitatoiHuomautukset(queryClient),
  });
}

export function useDeleteEntryNote() {
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (id) => api.delete(`/api/entry-notes/${id}`),
    onSuccess: () => mitatoiHuomautukset(queryClient),
  });
}

// --- Kuukauden lukitus -----------------------------------------------------

export const lockKeys = {
  months: (year: number) => ['month-locks', year] as const,
  events: (year: number) => ['month-locks', year, 'events'] as const,
  checklist: (monthKey: string) => ['month-locks', monthKey, 'checklist'] as const,
};

export function useMonthLocks(year: number): UseQueryResult<MonthLockSummaryDto[], ApiError> {
  return useQuery<MonthLockSummaryDto[], ApiError>({
    queryKey: lockKeys.months(year),
    queryFn: () => api.get<MonthLockSummaryDto[]>(`/api/month-locks?year=${year}`),
  });
}

export function useLockEvents(year: number): UseQueryResult<MonthLockEventDto[], ApiError> {
  return useQuery<MonthLockEventDto[], ApiError>({
    queryKey: lockKeys.events(year),
    queryFn: () => api.get<MonthLockEventDto[]>(`/api/month-lock-events?year=${year}`),
  });
}

export function useLockChecklist(
  monthKey: string | null,
): UseQueryResult<LockChecklistDto, ApiError> {
  return useQuery<LockChecklistDto, ApiError>({
    queryKey: lockKeys.checklist(monthKey ?? ''),
    queryFn: () => api.get<LockChecklistDto>(`/api/month-locks/${monthKey}/checklist`),
    enabled: Boolean(monthKey),
  });
}

/**
 * Lukituksen muutos mitätöi myös kalenterin ja kirjaukset: lukko muuttaa
 * sen, mitä käyttöliittymä saa tarjota tehtäväksi.
 */
export function useLockMutation() {
  const queryClient = useQueryClient();
  return useMutation<
    unknown,
    ApiError,
    { monthKey: string; action: 'lock' | 'unlock'; body: Record<string, unknown> }
  >({
    mutationFn: ({ monthKey, action, body }) =>
      api.post(`/api/month-locks/${monthKey}/${action}`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['month-locks'] });
      void queryClient.invalidateQueries({ queryKey: ['time-entries'] });
    },
  });
}

// --- Raportit --------------------------------------------------------------

export interface RaporttiJakso {
  from: IsoDate;
  to: IsoDate;
  includeInternal: boolean;
}

export const reportKeys = {
  summary: (jakso: RaporttiJakso, sort: string) => ['reports', 'summary', jakso, sort] as const,
  customer: (customerId: string, jakso: RaporttiJakso, groupBy: string) =>
    ['reports', 'summary', customerId, jakso, groupBy] as const,
  bounds: ['reports', 'range-bounds'] as const,
  mySummary: (jakso: RaporttiJakso) => ['reports', 'my-summary', jakso] as const,
};

function jaksoParams(jakso: RaporttiJakso): URLSearchParams {
  return new URLSearchParams({
    from: jakso.from,
    to: jakso.to,
    includeInternal: String(jakso.includeInternal),
  });
}

export function useReportSummary(
  jakso: RaporttiJakso,
  sort: 'value' | 'hours' | 'name',
): UseQueryResult<ReportSummaryDto, ApiError> {
  const params = jaksoParams(jakso);
  params.set('sort', sort);
  return useQuery<ReportSummaryDto, ApiError>({
    queryKey: reportKeys.summary(jakso, sort),
    queryFn: () => api.get<ReportSummaryDto>(`/api/reports/summary?${params.toString()}`),
  });
}

/**
 * Työntekijäkohtainen erittely (12.6). Vain ADMIN — kutsuja ratkaisee
 * `enabled`illä, ettei muille lähde 403:een päättyvää kyselyä joka
 * raporttilatauksella.
 */
export function useUtilization(
  jakso: RaporttiJakso,
  enabled: boolean,
): UseQueryResult<UtilizationReportDto, ApiError> {
  const params = jaksoParams(jakso);
  return useQuery<UtilizationReportDto, ApiError>({
    queryKey: ['reports', 'utilization', jakso.from, jakso.to],
    queryFn: () => api.get<UtilizationReportDto>(`/api/reports/utilization?${params.toString()}`),
    enabled,
  });
}

/**
 * Porautuminen yhden työntekijän tunteihin asiakkaittain (12.6). Kysely
 * lähtee vasta kun rivi avataan: erittelyssä on kymmeniä rivejä, eikä
 * niitä kaikkia kannata hakea sen varalta, että joku klikkaa yhtä.
 */
export function useUtilizationUser(
  userId: string | null,
  jakso: RaporttiJakso,
): UseQueryResult<UtilizationUserDetailDto, ApiError> {
  const params = jaksoParams(jakso);
  return useQuery<UtilizationUserDetailDto, ApiError>({
    queryKey: ['reports', 'utilization', 'user', userId, jakso.from, jakso.to],
    queryFn: () =>
      api.get<UtilizationUserDetailDto>(
        `/api/reports/utilization/users/${userId}?${params.toString()}`,
      ),
    enabled: userId !== null,
  });
}

export function useReportCustomer(
  customerId: string,
  jakso: RaporttiJakso,
  groupBy: ReportGroupBy,
): UseQueryResult<ReportCustomerDetailDto, ApiError> {
  const params = jaksoParams(jakso);
  params.set('groupBy', groupBy);
  return useQuery<ReportCustomerDetailDto, ApiError>({
    queryKey: reportKeys.customer(customerId, jakso, groupBy),
    queryFn: () =>
      api.get<ReportCustomerDetailDto>(
        `/api/reports/summary/customers/${customerId}?${params.toString()}`,
      ),
    enabled: Boolean(customerId),
  });
}

export function useRangeBounds(): UseQueryResult<RangeBoundsDto, ApiError> {
  return useQuery<RangeBoundsDto, ApiError>({
    queryKey: reportKeys.bounds,
    queryFn: () => api.get<RangeBoundsDto>('/api/reports/range-bounds'),
    staleTime: 10 * 60 * 1000,
  });
}

export function useMySummary(jakso: RaporttiJakso): UseQueryResult<MySummaryDto, ApiError> {
  return useQuery<MySummaryDto, ApiError>({
    queryKey: reportKeys.mySummary(jakso),
    queryFn: () => api.get<MySummaryDto>(`/api/reports/my-summary?${jaksoParams(jakso)}`),
  });
}

/** Kuittaus ja sen peruminen mitätöivät raportin molemmat tasot. */
export function useCheckMutation() {
  const queryClient = useQueryClient();
  return useMutation<
    CheckStateDto,
    ApiError,
    { customerId: string; monthKey: string; kuittaa: boolean; note?: string }
  >({
    mutationFn: ({ customerId, monthKey, kuittaa, note }) =>
      kuittaa
        ? api.put<CheckStateDto>(`/api/customers/${customerId}/checks/${monthKey}`, {
            note: note ?? null,
          })
        : api.delete<CheckStateDto>(`/api/customers/${customerId}/checks/${monthKey}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['reports'] });
      void queryClient.invalidateQueries({ queryKey: ['month-locks'] });
    },
  });
}

// --- Hinnasto ja muutosloki ------------------------------------------------

export function useRateOverview(): UseQueryResult<RateOverviewDto, ApiError> {
  return useQuery<RateOverviewDto, ApiError>({
    queryKey: ['rates', 'overview'],
    queryFn: () => api.get<RateOverviewDto>('/api/rates/overview'),
  });
}

/**
 * Takautuva hintalaskenta. Kuivaharjoitus ei mitätöi mitään — se ei muuta
 * mitään. Varsinainen ajo mitätöi kaiken, koska se kirjoittaa uusiksi
 * kirjausten arvon.
 */
export function useRecalculate() {
  const queryClient = useQueryClient();
  return useMutation<RateRecalculationDto, ApiError, RateRecalculateInput>({
    mutationFn: (input) => api.post<RateRecalculationDto>('/api/rates/recalculate', input),
    onSuccess: (result) => {
      if (result.dryRun) return;
      void queryClient.invalidateQueries({ queryKey: ['rates'] });
      void queryClient.invalidateQueries({ queryKey: ['reports'] });
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
      void queryClient.invalidateQueries({ queryKey: ['time-entries'] });
    },
  });
}

export function useAuditLog(suodattimet: {
  action?: string;
  targetType?: string;
}): UseQueryResult<AuditLogEntryDto[], ApiError> {
  const params = new URLSearchParams({ limit: '100' });
  if (suodattimet.action) params.set('action', suodattimet.action);
  if (suodattimet.targetType) params.set('targetType', suodattimet.targetType);

  return useQuery<AuditLogEntryDto[], ApiError>({
    queryKey: ['audit-log', suodattimet],
    queryFn: () => api.get<AuditLogEntryDto[]>(`/api/audit-log?${params.toString()}`),
  });
}

// --- Järjestelmäasetukset --------------------------------------------------

/**
 * Asetusten tallennus (suunnitelma 7). Onnistuminen mitätöi istunnon:
 * valuutta tulee käyttöliittymään `/api/me`:n kautta, ja ilman mitätöintiä
 * uusi valuutta näkyisi vasta seuraavalla kirjautumisella. Muut käyttäjät
 * saavat sen omalla seuraavalla latauksellaan — asetus on palvelimella,
 * ei selaimessa.
 */
export function useUpdateSettings() {
  const queryClient = useQueryClient();
  return useMutation<AppSettingsPublic, ApiError, SettingsUpdateInput>({
    mutationFn: (input) => api.patch<AppSettingsPublic>('/api/settings', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['me'] });
      // Yrityksen nimi näkyy myös kirjautumissivulla, joka lukee sen
      // istunnottomasta `/api/branding`:sta eikä `/api/me`:stä.
      void queryClient.invalidateQueries({ queryKey: ['branding'] });
      // Muistutusten asetukset ja niiden esikatselut tulevat omalta
      // päätteeltään: yrityksen nimi näkyy myös viestin allekirjoituksessa.
      void queryClient.invalidateQueries({ queryKey: ['reminders'] });
      // Laskutustavoite tulee omalta päätteeltään, ja se näkyy myös
      // raportin työntekijäerittelyssä.
      void queryClient.invalidateQueries({ queryKey: ['settings'] });
      void queryClient.invalidateQueries({ queryKey: ['reports'] });
    },
  });
}

/**
 * Vain ADMINille näkyvät asetukset (12.6). Oma päätteensä, koska
 * `/api/me`:n asetukset lähtevät jokaiselle kirjautuneelle.
 */
export function useAdminSettings(): UseQueryResult<AdminSettingsDto, ApiError> {
  return useQuery<AdminSettingsDto, ApiError>({
    queryKey: ['settings', 'admin'],
    queryFn: () => api.get<AdminSettingsDto>('/api/settings/admin'),
  });
}

// --- Muistutusten asetukset (backlog 3.1) ---------------------------------

export function useReminderSettings(): UseQueryResult<ReminderSettingsDto, ApiError> {
  return useQuery<ReminderSettingsDto, ApiError>({
    queryKey: ['reminders', 'settings'],
    queryFn: () => api.get<ReminderSettingsDto>('/api/reminders/settings'),
  });
}

/**
 * Esikatselu haetaan vasta pyydettäessä (`enabled`), koska se on kolme
 * kyselyä lisää joka kerta, kun asetussivu avataan — eikä viestiä lueta
 * joka kerralla.
 */
export function useReminderPreview(
  kind: ReminderKind | null,
): UseQueryResult<ReminderPreviewDto, ApiError> {
  return useQuery<ReminderPreviewDto, ApiError>({
    queryKey: ['reminders', 'preview', kind ?? ''],
    queryFn: () => api.get<ReminderPreviewDto>(`/api/reminders/preview?kind=${kind}`),
    enabled: kind !== null,
  });
}

export function useSendReminderTest() {
  return useMutation<{ sentTo: string }, ApiError, ReminderKind>({
    mutationFn: (kind) => api.post<{ sentTo: string }>('/api/reminders/test', { kind }),
  });
}

// --- API-tunnukset ---------------------------------------------------------

/**
 * Tunnusten listaus (suunnitelma 9.5). Ei sisällä tunnuksia itseään —
 * niitä ei ole kannassakaan, vain tiivisteet.
 */
export function useApiTokens(): UseQueryResult<ApiTokenDto[], ApiError> {
  return useQuery<ApiTokenDto[], ApiError>({
    queryKey: ['api-tokens'],
    queryFn: () => api.get<ApiTokenDto[]>('/api/api-tokens'),
  });
}

/**
 * Luonti palauttaa tunnuksen kertaalleen. Vastausta ei laiteta
 * välimuistiin: se sisältää salaisuuden, ja välimuistissa se jäisi
 * elämään pidemmäksi aikaa kuin sitä on tarkoitus näyttää.
 */
export function useCreateApiToken() {
  const queryClient = useQueryClient();
  return useMutation<ApiTokenCreatedDto, ApiError, ApiTokenCreateInput>({
    mutationFn: (input) => api.post<ApiTokenCreatedDto>('/api/api-tokens', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['api-tokens'] });
    },
  });
}

export function useRevokeApiToken() {
  const queryClient = useQueryClient();
  return useMutation<ApiTokenDto, ApiError, string>({
    mutationFn: (id) => api.delete<ApiTokenDto>(`/api/api-tokens/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['api-tokens'] });
    },
  });
}

// --- Yrityksen tunnus ------------------------------------------------------

/**
 * Tunnus ja yrityksen nimi. Sama kysely kirjautumissivulle ja
 * sovellukseen: pääte on auki ilman istuntoa, joten kirjautumaton sivu ei
 * tarvitse omaa polkuaan.
 *
 * Kuvan tavut eivät kulje tästä vaan `<img>`-elementissä — selaimen
 * kuvavälimuisti hoitaa sen paremmin kuin kyselykirjasto.
 */
export function useBranding(): UseQueryResult<BrandingDto, ApiError> {
  return useQuery<BrandingDto, ApiError>({
    queryKey: ['branding'],
    queryFn: () => api.get<BrandingDto>('/api/branding'),
    staleTime: 5 * 60 * 1000,
  });
}

export interface LogoTallennus extends LogoUploadInput {
  variant: LogoVariant;
}

export function useUploadLogo() {
  const queryClient = useQueryClient();
  return useMutation<BrandingDto, ApiError, LogoTallennus>({
    mutationFn: ({ variant, ...body }) =>
      api.put<BrandingDto>(`/api/branding/logo/${variant}`, body),
    onSuccess: (data) => {
      queryClient.setQueryData(['branding'], data);
    },
  });
}

export function useDeleteLogo() {
  const queryClient = useQueryClient();
  return useMutation<BrandingDto, ApiError, LogoVariant>({
    mutationFn: (variant) => api.delete<BrandingDto>(`/api/branding/logo/${variant}`),
    onSuccess: (data) => {
      queryClient.setQueryData(['branding'], data);
    },
  });
}

// --- Koulutukset (docs/KOULUTUKSET.md) -------------------------------------

export const trainingKeys = {
  list: ['trainings'] as const,
  alerts: ['trainings', 'alerts'] as const,
  detail: (code: string) => ['trainings', 'detail', code] as const,
  attempt: (id: string) => ['exam-attempts', id] as const,
  result: (id: string) => ['exam-attempts', id, 'result'] as const,
  admin: ['trainings', 'admin'] as const,
  audience: (id: string) => ['trainings', 'admin', id, 'users'] as const,
  report: (params: string) => ['trainings', 'report', params] as const,
};

/**
 * Koulutuksen tila muuttuu monesta suunnasta: osion lukemisesta, tentin
 * palautuksesta, hallinnan kohdennuksesta. Mitätöinti tehdään yhdestä
 * paikasta, jotta uusi mutaatio ei voi unohtaa varoituspalkkia — se on se,
 * joka jäisi näyttämään vanhaa tietoa pisimpään.
 */
function mitatoiKoulutukset(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: ['trainings'] });
}

export function useTrainings(): UseQueryResult<TrainingListItemDto[], ApiError> {
  return useQuery<TrainingListItemDto[], ApiError>({
    queryKey: trainingKeys.list,
    queryFn: () => api.get<TrainingListItemDto[]>('/api/trainings'),
  });
}

/**
 * Varoituspalkin yhteenveto.
 *
 * Kevyt kysely, joka ajetaan joka sivulla — siksi vanhenemisaika on pitkä.
 * Puuttuva koulutus ei muutu minuutissa, ja mutaatiot mitätöivät tämän
 * joka tapauksessa.
 */
export function useTrainingAlerts(): UseQueryResult<TrainingAlertsDto, ApiError> {
  return useQuery<TrainingAlertsDto, ApiError>({
    queryKey: trainingKeys.alerts,
    queryFn: () => api.get<TrainingAlertsDto>('/api/trainings/alerts'),
    staleTime: 10 * 60 * 1000,
  });
}

export function useTraining(code: string): UseQueryResult<TrainingDetailDto, ApiError> {
  return useQuery<TrainingDetailDto, ApiError>({
    queryKey: trainingKeys.detail(code),
    queryFn: () => api.get<TrainingDetailDto>(`/api/trainings/${code}`),
  });
}

/**
 * Osio luetuksi.
 *
 * Vastaus on koko koulutuksen tila, joten se kirjoitetaan välimuistiin
 * suoraan: lukumerkintä tehdään jokaisen osion lopussa, ja uusi haku
 * jokaisen jälkeen vilkuttaisi sivua turhaan.
 */
export function useMarkSectionRead(code: string) {
  const queryClient = useQueryClient();
  return useMutation<TrainingDetailDto, ApiError, string>({
    mutationFn: (sectionKey) =>
      api.post<TrainingDetailDto>(`/api/trainings/${code}/sections/${sectionKey}/read`),
    onSuccess: (data) => {
      queryClient.setQueryData(trainingKeys.detail(code), data);
      void queryClient.invalidateQueries({ queryKey: trainingKeys.list });
      void queryClient.invalidateQueries({ queryKey: trainingKeys.alerts });
    },
  });
}

export function useStartExam(code: string) {
  const queryClient = useQueryClient();
  return useMutation<ExamAttemptDto, ApiError, void>({
    mutationFn: () => api.post<ExamAttemptDto>(`/api/trainings/${code}/exam`),
    onSuccess: (attempt) => {
      queryClient.setQueryData(trainingKeys.attempt(attempt.id), attempt);
      mitatoiKoulutukset(queryClient);
    },
  });
}

/**
 * Kesken oleva tentti.
 *
 * `staleTime: Infinity` ja ei uudelleenhakua ikkunan fokusoinnista: paperi
 * on jäädytetty eikä muutu, ja taustalla tehty haku kesken tentin
 * korvaisi käyttäjän valinnat palvelimen versiolla juuri sillä hetkellä,
 * kun hän on vaihtamassa vastausta.
 */
export function useExamAttempt(id: string | null): UseQueryResult<ExamAttemptDto, ApiError> {
  return useQuery<ExamAttemptDto, ApiError>({
    queryKey: trainingKeys.attempt(id ?? ''),
    queryFn: () => api.get<ExamAttemptDto>(`/api/exam-attempts/${id}`),
    enabled: id !== null,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/**
 * Yhden kysymyksen vastaus.
 *
 * Tallennetaan heti valinnasta. Selaimen kaatuminen tai suljettu välilehti
 * ei saa viedä mukanaan kahtakymmentä minuuttia työtä, ja juuri siksi
 * umpeutunut tenttikin voidaan arvostella annetuista vastauksista.
 *
 * Monivalinnassa lähetetään **koko valintajoukko**, ei yhden rastin
 * muutos. Kutsuja tietää valinnat jo optimistisesta välimuistista, ja
 * joukko tekee tallennuksesta idempotentin: kahdesti mennyt pyyntö
 * päätyy samaan tilaan, toisin kuin "käännä tämä rasti".
 */
export function useSaveExamAnswer(attemptId: string) {
  const queryClient = useQueryClient();
  return useMutation<
    { answeredCount: number; questionCount: number },
    ApiError,
    { questionKey: string } & ExamAnswerInput
  >({
    mutationFn: ({ questionKey, choiceKeys }) =>
      api.put<{ answeredCount: number; questionCount: number }>(
        `/api/exam-attempts/${attemptId}/answers/${questionKey}`,
        { choiceKeys },
      ),
    onMutate: ({ questionKey, choiceKeys }) => {
      // Valinta näkyy heti. Palvelin on totuus tuloksesta, muttei siitä,
      // mitä käyttäjä juuri klikkasi.
      queryClient.setQueryData<ExamAttemptDto>(trainingKeys.attempt(attemptId), (vanha) =>
        vanha
          ? {
              ...vanha,
              questions: vanha.questions.map((question) =>
                question.key === questionKey
                  ? { ...question, selectedChoiceKeys: choiceKeys }
                  : question,
              ),
            }
          : vanha,
      );
    },
  });
}

export function useSubmitExam(attemptId: string) {
  const queryClient = useQueryClient();
  return useMutation<ExamResultDto, ApiError, void>({
    mutationFn: () => api.post<ExamResultDto>(`/api/exam-attempts/${attemptId}/submit`),
    onSuccess: (tulos) => {
      queryClient.setQueryData(trainingKeys.result(attemptId), tulos);
      mitatoiKoulutukset(queryClient);
    },
  });
}

export function useExamResult(id: string | null): UseQueryResult<ExamResultDto, ApiError> {
  return useQuery<ExamResultDto, ApiError>({
    queryKey: trainingKeys.result(id ?? ''),
    queryFn: () => api.get<ExamResultDto>(`/api/exam-attempts/${id}/result`),
    enabled: id !== null,
    retry: false,
  });
}

// --- Koulutusten hallinta --------------------------------------------------

export function useAdminTrainings(): UseQueryResult<TrainingAdminDto[], ApiError> {
  return useQuery<TrainingAdminDto[], ApiError>({
    queryKey: trainingKeys.admin,
    queryFn: () => api.get<TrainingAdminDto[]>('/api/admin/trainings'),
  });
}

export function useUpdateTrainingSettings() {
  const queryClient = useQueryClient();
  return useMutation<TrainingAdminDto, ApiError, { id: string } & TrainingSettingsInput>({
    mutationFn: ({ id, ...input }) =>
      api.patch<TrainingAdminDto>(`/api/admin/trainings/${id}`, input),
    onSuccess: () => mitatoiKoulutukset(queryClient),
  });
}

export function useUpdateTrainingAssignments() {
  const queryClient = useQueryClient();
  return useMutation<TrainingAdminDto, ApiError, { id: string } & TrainingAssignmentsInput>({
    mutationFn: ({ id, assignments }) =>
      api.put<TrainingAdminDto>(`/api/admin/trainings/${id}/assignments`, { assignments }),
    onSuccess: () => mitatoiKoulutukset(queryClient),
  });
}

export function useRecordTrainingCompletion() {
  const queryClient = useQueryClient();
  return useMutation<TrainingAdminDto, ApiError, { id: string } & TrainingCompletionRecordInput>({
    mutationFn: ({ id, ...input }) =>
      api.post<TrainingAdminDto>(`/api/admin/trainings/${id}/completions`, input),
    onSuccess: () => mitatoiKoulutukset(queryClient),
  });
}

/**
 * Yhden koulutuksen kohdejoukko nimettynä.
 *
 * Haetaan vasta kun ikkuna avataan (`enabled`): hallintasivulla on rivi
 * koulutusta kohti, eikä nimilistaa kannata hakea kymmeneen korttiin
 * siltä varalta, että yhtä lukua klikataan.
 */
export function useTrainingAudience(
  id: string | null,
): UseQueryResult<TrainingAudienceDto, ApiError> {
  return useQuery<TrainingAudienceDto, ApiError>({
    queryKey: trainingKeys.audience(id ?? ''),
    queryFn: () => api.get<TrainingAudienceDto>(`/api/admin/trainings/${id}/users`),
    enabled: id !== null,
  });
}

export function useSyncTrainings() {
  const queryClient = useQueryClient();
  return useMutation<TrainingSyncResultDto, ApiError, void>({
    mutationFn: () => api.post<TrainingSyncResultDto>('/api/admin/trainings/sync'),
    onSuccess: () => mitatoiKoulutukset(queryClient),
  });
}

export interface KoulutusRaporttiSuodatin {
  trainingId: string | null;
  includeInactive: boolean;
  onlyProblems: boolean;
}

export function raporttiParametrit(suodatin: KoulutusRaporttiSuodatin): string {
  const params = new URLSearchParams();
  if (suodatin.trainingId) params.set('trainingId', suodatin.trainingId);
  if (suodatin.includeInactive) params.set('includeInactive', 'true');
  if (suodatin.onlyProblems) params.set('onlyProblems', 'true');
  return params.toString();
}

export function useTrainingReport(
  suodatin: KoulutusRaporttiSuodatin,
): UseQueryResult<TrainingReportDto, ApiError> {
  const params = raporttiParametrit(suodatin);
  return useQuery<TrainingReportDto, ApiError>({
    queryKey: trainingKeys.report(params),
    queryFn: () => api.get<TrainingReportDto>(`/api/admin/training-report?${params}`),
  });
}

// --- Projektinhallinta (docs/PROJEKTINHALLINTA.md) --------------------------

export type BoardKey = { laji: 'asiakas' | 'projekti'; id: string };

export function useBoardList(): UseQueryResult<BoardListResponse, ApiError> {
  return useQuery<BoardListResponse, ApiError>({
    queryKey: ['boards'],
    queryFn: () => api.get<BoardListResponse>('/api/boards'),
  });
}

export function useBoard(key: BoardKey): UseQueryResult<BoardResponse, ApiError> {
  return useQuery<BoardResponse, ApiError>({
    queryKey: ['boards', key.laji, key.id],
    queryFn: () =>
      api.get<BoardResponse>(
        key.laji === 'asiakas' ? `/api/boards/customer/${key.id}` : `/api/boards/project/${key.id}`,
      ),
  });
}

/**
 * Toisen taulun uimaradat tehtävän siirtoa varten (luku 13). Sama kysely
 * kuin taululla, mutta vasta kun kohdetaulu on valittu.
 */
export function useOptionalBoard(key: BoardKey | null): UseQueryResult<BoardResponse, ApiError> {
  return useQuery<BoardResponse, ApiError>({
    queryKey: ['boards', key?.laji, key?.id],
    queryFn: () =>
      api.get<BoardResponse>(
        key!.laji === 'asiakas'
          ? `/api/boards/customer/${key!.id}`
          : `/api/boards/project/${key!.id}`,
      ),
    enabled: key !== null,
  });
}

/**
 * Asiakasraportti (luku 27). Avain sisältää valinnat, joten jokainen
 * yhdistelmä on oma välimuistirivinsä ja valinnan vaihto hakee uuden.
 * `['boards', …]`-etuliite: taulun muutos vanhentaa raportin.
 */
export function useCustomerReport(
  query: Omit<CustomerReportQuery, 'projectId'> & { projectId: string | null },
): UseQueryResult<CustomerReportDto, ApiError> {
  const params = new URLSearchParams({
    customerId: query.customerId,
    scope: query.scope,
    completedDays: String(query.completedDays),
    hours: String(query.hours),
    lang: query.lang,
  });
  if (query.projectId) params.set('projectId', query.projectId);
  return useQuery<CustomerReportDto, ApiError>({
    queryKey: ['boards', 'report', params.toString()],
    queryFn: () => api.get<CustomerReportDto>(`/api/boards/report?${params.toString()}`),
    // Edellinen raportti näkyy himmennettynä, kunnes valinnan mukainen on haettu.
    placeholderData: (edellinen) => edellinen,
  });
}

/**
 * Tehtävän kommentit (luku 6). Haetaan ikkunan avautuessa eikä taulun
 * mukana: taulu kantaa vain määrän, koska sata korttia kommentteineen
 * olisi turhaa kuormaa jokaiseen siirtoon.
 */
export function useTaskComments(taskId: string): UseQueryResult<KanbanTaskCommentDto[], ApiError> {
  return useQuery<KanbanTaskCommentDto[], ApiError>({
    queryKey: ['task-comments', taskId],
    queryFn: () => api.get<KanbanTaskCommentDto[]>(`/api/tasks/${taskId}/comments`),
  });
}

/** Omat tehtävät kaikilta tauluilta (luku 17). */
export function useMyTasks(): UseQueryResult<MyTasksResponse, ApiError> {
  return useQuery<MyTasksResponse, ApiError>({
    queryKey: ['boards', 'mine'],
    queryFn: () => api.get<MyTasksResponse>('/api/tasks/mine'),
  });
}

/** Tehtävä ja sen taulu tunnisteella (luku 13); kalenterin esitäyttö ja lyhytosoite. */
export function useTaskLookup(taskId: string | null): UseQueryResult<TaskLookupDto, ApiError> {
  return useQuery<TaskLookupDto, ApiError>({
    queryKey: ['task-lookup', taskId],
    queryFn: () => api.get<TaskLookupDto>(`/api/tasks/${taskId}`),
    enabled: Boolean(taskId),
    retry: false,
  });
}

/** Tilahistoria ja kestot osioittain (luku 18). */
export function useTaskHistory(taskId: string): UseQueryResult<TaskHistoryDto, ApiError> {
  return useQuery<TaskHistoryDto, ApiError>({
    queryKey: ['task-history', taskId],
    queryFn: () => api.get<TaskHistoryDto>(`/api/tasks/${taskId}/history`),
  });
}

/** Tehtävältä kopioidut kirjaukset kutsujan näkyvyysalueella (luku 16). */
export function useTaskHours(taskId: string): UseQueryResult<TaskHoursDto, ApiError> {
  return useQuery<TaskHoursDto, ApiError>({
    queryKey: ['task-hours', taskId],
    queryFn: () => api.get<TaskHoursDto>(`/api/tasks/${taskId}/hours`),
  });
}

/** Tehtävän relaatiot (luku 6), haetaan ikkunan avautuessa kuten kommentit. */
export function useTaskRelations(taskId: string): UseQueryResult<TaskRelationDto[], ApiError> {
  return useQuery<TaskRelationDto[], ApiError>({
    queryKey: ['task-relations', taskId],
    queryFn: () => api.get<TaskRelationDto[]>(`/api/tasks/${taskId}/relations`),
  });
}

/**
 * Tehtävän muistutukset (luku 24): odottavat ja hiljattain lähetetyt.
 * Haetaan ikkunan avautuessa kuten kommentit ja relaatiot; taululla on
 * niistä vain kortin kello.
 */
export function useTaskReminders(taskId: string): UseQueryResult<TaskReminderDto[], ApiError> {
  return useQuery<TaskReminderDto[], ApiError>({
    queryKey: ['task-reminders', taskId],
    queryFn: () => api.get<TaskReminderDto[]>(`/api/tasks/${taskId}/reminders`),
  });
}

/**
 * Muistutuksen asetus, siirto tai peruminen. Mitätöi tehtävän listan ja
 * taulun: kortin kello muuttuu samalla, ja koontinäkymä (`boards/mine`)
 * on saman perheen alla.
 */
export function useReminderMutation<TResult, TInput>(
  taskId: string,
  mutate: (input: TInput) => Promise<TResult>,
) {
  const queryClient = useQueryClient();
  return useMutation<TResult, ApiError, TInput>({
    mutationFn: mutate,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['task-reminders', taskId] });
      void queryClient.invalidateQueries({ queryKey: ['boards'] });
    },
  });
}

/** Relaation lisäys tai poisto mitätöi listan ja taulun (kortin merkit). */
export function useRelationMutation<TResult, TInput>(mutate: (input: TInput) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation<TResult, ApiError, TInput>({
    mutationFn: mutate,
    onSuccess: () => {
      // Kaikki relaatiolistat, ei vain tämän tehtävän: relaatio näkyy
      // molemmilla osapuolilla.
      void queryClient.invalidateQueries({ queryKey: ['task-relations'] });
      void queryClient.invalidateQueries({ queryKey: ['boards'] });
    },
  });
}

/** Tehtävähaku relaation lisäykseen; tyhjä haku ei lähde palvelimelle. */
export function useTaskSearch(
  customerId: string,
  q: string,
  exclude: string,
): UseQueryResult<TaskRefDto[], ApiError> {
  const haku = q.trim();
  return useQuery<TaskRefDto[], ApiError>({
    queryKey: ['task-search', customerId, haku, exclude],
    queryFn: () =>
      api.get<TaskRefDto[]>(
        `/api/tasks/search?customerId=${customerId}&q=${encodeURIComponent(haku)}&exclude=${exclude}`,
      ),
    enabled: haku.length > 0,
  });
}

/** Kommentin kirjoitus mitätöi listan ja taulun (kortin kommenttimäärä). */
export function useCommentMutation<TResult, TInput>(
  taskId: string,
  mutate: (input: TInput) => Promise<TResult>,
) {
  const queryClient = useQueryClient();
  return useMutation<TResult, ApiError, TInput>({
    mutationFn: mutate,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['task-comments', taskId] });
      void queryClient.invalidateQueries({ queryKey: ['boards'] });
    },
  });
}

/**
 * Taulun avaaminen viimeksi käytettyjen listaan (luku 13). Lista
 * mitätöidään täsmälleen (`exact`), jotta avoin taulu ei lataudu
 * uudelleen omasta käynnistään.
 */
export function useRecordBoardVisit() {
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, BoardKeyDto>({
    mutationFn: (key) => api.post<void>('/api/boards/visits', key),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['boards'], exact: true });
    },
  });
}

/**
 * Taulun kirjoitukset mitätöivät koko `boards`-perheen: listan
 * tehtävämäärät ja avoimen taulun. Palvelin numeroi sijainnit uudelleen
 * jokaisessa siirrossa, joten paikallinen optimistinen päivitys arvaisi
 * väärin juuri niissä tapauksissa, joissa järjestys muuttuu eniten.
 */
export function useBoardMutation<TResult, TInput>(mutate: (input: TInput) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation<TResult, ApiError, TInput>({
    mutationFn: mutate,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['boards'] });
      // Siirto, deadline ja vastuu kirjautuvat historiaan (luvut 18 ja 29).
      void queryClient.invalidateQueries({ queryKey: ['task-history'] });
    },
  });
}

// --- Kumppanit (PROJEKTINHALLINTA 29) -------------------------------------------

/** Kumppanirekisteri yhteyshenkilöineen; aktiiviset ensin. */
export function usePartners(): UseQueryResult<PartnerDto[], ApiError> {
  return useQuery<PartnerDto[], ApiError>({
    queryKey: ['partners'],
    queryFn: () => api.get<PartnerDto[]>('/api/partners'),
    staleTime: 60 * 1000,
  });
}

export function usePartnerMutation<TResult, TInput>(mutate: (input: TInput) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation<TResult, ApiError, TInput>({
    mutationFn: mutate,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['partners'] });
      // Nimen muutos näkyy korteilla.
      void queryClient.invalidateQueries({ queryKey: ['boards'] });
    },
  });
}

// --- Asiakkaan yhteyshenkilöt ja dokumenttilinkit (suunnitelma 8) ----------

export const customerDetailKeys = {
  contacts: (customerId: string) => ['customer-contacts', customerId] as const,
  links: (customerId: string) => ['customer-links', customerId] as const,
  tags: ['customer-link-tags'] as const,
};

export function useCustomerContacts(
  customerId: string | null,
): UseQueryResult<CustomerContactDto[], ApiError> {
  return useQuery<CustomerContactDto[], ApiError>({
    queryKey: customerDetailKeys.contacts(customerId ?? ''),
    queryFn: () => api.get<CustomerContactDto[]>(`/api/customers/${customerId}/contacts`),
    enabled: Boolean(customerId),
  });
}

export function useCustomerLinks(
  customerId: string | null,
): UseQueryResult<CustomerLinkDto[], ApiError> {
  return useQuery<CustomerLinkDto[], ApiError>({
    queryKey: customerDetailKeys.links(customerId ?? ''),
    queryFn: () => api.get<CustomerLinkDto[]>(`/api/customers/${customerId}/links`),
    enabled: Boolean(customerId),
  });
}

/**
 * Talon tagit ehdotuksiksi. Ladataan vasta kun linkkilomake on auki:
 * luettelo on koko talon eikä muutu kesken muokkauksen.
 */
export function useCustomerLinkTags(
  enabled: boolean,
): UseQueryResult<CustomerLinkTagDto[], ApiError> {
  return useQuery<CustomerLinkTagDto[], ApiError>({
    queryKey: customerDetailKeys.tags,
    queryFn: () => api.get<CustomerLinkTagDto[]>('/api/customer-links/tags'),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Yhteyshenkilön, linkin tai kuvauksen kirjoitus.
 *
 * Mitätöi asiakkaan itsensä, koska kuvaus on sen kentässä, ja tagiluettelon,
 * koska uusi tagi kuuluu seuraavan lomakkeen ehdotuksiin.
 */
export function useCustomerDetailMutation<TResult, TInput>(
  customerId: string,
  mutate: (input: TInput) => Promise<TResult>,
) {
  const queryClient = useQueryClient();
  return useMutation<TResult, ApiError, TInput>({
    mutationFn: mutate,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: customerDetailKeys.contacts(customerId) });
      void queryClient.invalidateQueries({ queryKey: customerDetailKeys.links(customerId) });
      void queryClient.invalidateQueries({ queryKey: customerDetailKeys.tags });
      void queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}

// --- Kehitys ja osaaminen (docs/KEHITYSSUUNNITELMAT.md) ---------------------

const kehitysKeys = {
  oma: ['development', 'me'] as const,
  henkilo: (userId: string) => ['development', 'user', userId] as const,
  luettelo: ['certifications'] as const,
  polut: ['development', 'paths'] as const,
  luetteloAdmin: ['certifications', 'admin'] as const,
  raportti: (params: string) => ['certifications', 'report', params] as const,
};

/**
 * Saavutuksen kirjaus ja vahvistus muuttavat kolmea näkymää: omaa sivua,
 * alaisen sivua ja raporttia. Mitätöinti tehdään yhdestä paikasta, jotta
 * uusi mutaatio ei voi unohtaa yhtä niistä — sama kuvio kuin koulutuksilla.
 */
function mitatoiKehitys(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: ['development'] });
  void queryClient.invalidateQueries({ queryKey: ['certifications'] });
}

export function useMyDevelopment(): UseQueryResult<MyDevelopmentDto, ApiError> {
  return useQuery<MyDevelopmentDto, ApiError>({
    queryKey: kehitysKeys.oma,
    queryFn: () => api.get<MyDevelopmentDto>('/api/me/development'),
  });
}

export function useUserDevelopment(userId: string): UseQueryResult<UserDevelopmentDto, ApiError> {
  return useQuery<UserDevelopmentDto, ApiError>({
    queryKey: kehitysKeys.henkilo(userId),
    queryFn: () => api.get<UserDevelopmentDto>(`/api/development/users/${userId}`),
    enabled: Boolean(userId),
  });
}

/** Valittavissa olevat sertifikaatit. Vain aktiiviset, ja ne muuttuvat harvoin. */
export function useCertifications(enabled = true): UseQueryResult<CertificationDto[], ApiError> {
  return useQuery<CertificationDto[], ApiError>({
    queryKey: kehitysKeys.luettelo,
    queryFn: () => api.get<CertificationDto[]>('/api/certifications'),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Kehityspolut askelineen (docs/KEHITYSSUUNNITELMAT.md, luku 16).
 *
 * Yksi haku kaikille poluille: niitä on muutama, ja polkusivulta toiselle
 * siirtyminen ei ansaitse uutta pyyntöä. Avain on `development`-puussa,
 * joten tavoitteen luonti mitätöi myös askelten tilanteen — muuten
 * "tavoitteena"-merkki ilmestyisi vasta sivun latauksella.
 */
export function useDevelopmentPaths(): UseQueryResult<DevelopmentPathDto[], ApiError> {
  return useQuery<DevelopmentPathDto[], ApiError>({
    queryKey: kehitysKeys.polut,
    queryFn: () => api.get<DevelopmentPathDto[]>('/api/development-paths'),
  });
}

/** Ylläpidon luettelo: myös passivoidut ja saavutusten määrä. */
export function useAdminCertifications(): UseQueryResult<CertificationDto[], ApiError> {
  return useQuery<CertificationDto[], ApiError>({
    queryKey: kehitysKeys.luetteloAdmin,
    queryFn: () => api.get<CertificationDto[]>('/api/admin/certifications'),
  });
}

export function useCertificationReport(
  suodattimet: Record<string, string | undefined>,
): UseQueryResult<CertificationReportDto, ApiError> {
  const params = new URLSearchParams(
    Object.entries(suodattimet).flatMap(([avain, arvo]) => (arvo ? [[avain, arvo]] : [])),
  ).toString();

  return useQuery<CertificationReportDto, ApiError>({
    queryKey: kehitysKeys.raportti(params),
    queryFn: () =>
      api.get<CertificationReportDto>(
        `/api/admin/certification-report${params ? `?${params}` : ''}`,
      ),
  });
}

export function useKehitysMutation<TResult, TInput>(mutate: (input: TInput) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation<TResult, ApiError, TInput>({
    mutationFn: mutate,
    onSuccess: () => mitatoiKehitys(queryClient),
  });
}

export function useDevelopmentReview(
  reviewId: string,
): UseQueryResult<DevelopmentReviewDto, ApiError> {
  return useQuery<DevelopmentReviewDto, ApiError>({
    queryKey: ['development', 'review', reviewId],
    queryFn: () => api.get<DevelopmentReviewDto>(`/api/development/reviews/${reviewId}`),
    enabled: Boolean(reviewId),
  });
}

export function useDevelopmentOverview(): UseQueryResult<DevelopmentOverviewDto, ApiError> {
  return useQuery<DevelopmentOverviewDto, ApiError>({
    queryKey: ['development', 'overview'],
    queryFn: () => api.get<DevelopmentOverviewDto>('/api/admin/development-overview'),
  });
}

// --- Salaisuuksien jako (docs/SALAISUUDET.md) --------------------------------

export function useSecrets(
  scope: SecretListQuery['scope'],
  state: SecretState | '',
): UseQueryResult<SharedSecretDto[], ApiError> {
  const params = new URLSearchParams({ scope });
  if (state) params.set('state', state);
  return useQuery<SharedSecretDto[], ApiError>({
    queryKey: ['secrets', scope, state],
    queryFn: () => api.get<SharedSecretDto[]>(`/api/secrets?${params.toString()}`),
    // Lukutieto on se, jota jakaja tulee katsomaan. Minuutin välein riittää
    // eikä kuormita: lista on lyhyt ja säilytysaika pitää sen lyhyenä.
    refetchInterval: 60 * 1000,
  });
}

/**
 * Luonti palauttaa linkin ja PIN:n **ainoan kerran**. Vastaus ei jää
 * mutaatiovälimuistiin: `gcTime: 0` poistaa sen heti, kun komponentti
 * ei enää kuuntele, ja näkymä pitää sen omassa tilassaan vain niin kauan
 * kuin dialogi on auki.
 */
export function useCreateSecret() {
  const queryClient = useQueryClient();
  return useMutation<SharedSecretCreatedDto, ApiError, SecretCreateInput>({
    mutationFn: (input) => api.post<SharedSecretCreatedDto>('/api/secrets', input),
    gcTime: 0,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['secrets'] });
    },
  });
}

export function useRevokeSecret() {
  const queryClient = useQueryClient();
  return useMutation<SharedSecretDto, ApiError, string>({
    mutationFn: (id) => api.post<SharedSecretDto>(`/api/secrets/${id}/revoke`),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['secrets'] });
    },
  });
}
